import { Loader2 } from 'lucide-react';
import { memo, type ReactNode, Suspense } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';

interface PageModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	description?: string;
	children: ReactNode;
}

/**
 * PageModal - Large modal dialog for settings/admin pages
 *
 * Features:
 * - 75% width on desktop, 100% on mobile
 * - 90% height with scrollable content
 * - Suspense boundary for lazy-loaded components
 * - ESC and click-outside to close
 * - Memoized to prevent re-renders when parent updates
 * - Only renders children when open (prevents useEffect spam)
 *
 * Usage:
 * ```tsx
 * <PageModal
 *   open={modal === 'security'}
 *   onOpenChange={closeModal}
 *   title="Security Settings"
 *   description="Manage your passwords and authentication"
 * >
 *   <SecurityPage />
 * </PageModal>
 * ```
 */
export const PageModal = memo(function PageModal({
	open,
	onOpenChange,
	title,
	description,
	children,
}: PageModalProps) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className="
					max-w-[95vw] sm:max-w-[90vw] lg:max-w-[75vw]
					max-h-[95vh] sm:max-h-[90vh]
					w-full
					p-0
					gap-0
					overflow-hidden
				"
			>
				{/* Header */}
				<DialogHeader className="px-6 pt-6 pb-4 border-b">
					<DialogTitle className="text-2xl">{title}</DialogTitle>
					{description && <DialogDescription>{description}</DialogDescription>}
				</DialogHeader>

				{/* Content with Suspense for lazy loading - only render when open */}
				<ScrollArea className="flex-1 max-h-[calc(90vh-8rem)]">
					{open && (
						<Suspense
							fallback={
								<div className="flex items-center justify-center py-12">
									<Loader2 className="size-8 animate-spin text-muted-foreground" />
								</div>
							}
						>
							<div className="px-6 py-4">{children}</div>
						</Suspense>
					)}
				</ScrollArea>
			</DialogContent>
		</Dialog>
	);
});
